/**
 * FiscalMandatePage — Batch #14.236
 *
 * Page PUBLIQUE (magic link, aucune connexion) : /mandat-fiscal/:token
 * Le client remplit le Formulaire de renseignements fiscaux en ligne + signe,
 * OU télécharge le PDF vierge à imprimer et retourner.
 */
import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  FileSignature, Printer, PenLine, CheckCircle2, Loader2, Plus, Trash2, Eraser,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { API } from "@/api";

const NAVY = "#072354";

const MARITAL = ["Célibataire", "Conjoint(e) de fait", "Marié(e)", "Séparé(e)", "Divorcé(e)", "Veuf(veuve)"];
const SERVICES = [
  "Déclarations de revenus individuelles",
  "Déclarations de revenus de couple",
  "Déclarations de revenus monoparentales",
  "Déclarations d'immeuble à revenus (+ 65$/immeuble)",
];
const DEPOSIT = ["Déjà inscrit au dépôt direct", "Nouveau dépôt direct (joindre un spécimen de chèque)"];
const DOCUMENTS = [
  "Feuillets revenu d'emploi et prestation (T4, R1, T4E, CNESST, RRQ, etc.)",
  "Relevé d'intérêt et placements",
  "Relevé de REER / FTQ",
  "Relevé de frais de garde",
  "Reçu pour personne à charge",
  "Reçu médical",
  "Prime payée pour une assurance médicament",
  "Reçu de dons de bienfaisance",
  "Compte de taxes, facture entretien, intérêt sur prêt hypothécaire pour immeuble à revenus",
  "Autres documents pertinents",
];
const EMPTY_DEP = { full_name: "", sex: "", birth_date: "", relation: "", school: "", estimated_income: "" };

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900/30";

function Section({ num, title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 text-white text-sm font-bold" style={{ background: NAVY }}>
        {num ? `${num}. ` : ""}{title}
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function CheckList({ options, selected, onToggle, testPrefix }) {
  return (
    <div className="space-y-1.5">
      {options.map((o, i) => (
        <label key={o} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" checked={selected.includes(o)} onChange={() => onToggle(o)}
            className="w-4 h-4 mt-0.5 accent-blue-900" data-testid={`${testPrefix}-${i}`} />
          <span>{o}</span>
        </label>
      ))}
    </div>
  );
}

export function SignatureCanvas({ onChange }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasContent, setHasContent] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = NAVY;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
  }, []);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const isTouch = e.touches && e.touches.length > 0;
    const x = ((isTouch ? e.touches[0].clientX : e.clientX) - rect.left) * (c.width / rect.width);
    const y = ((isTouch ? e.touches[0].clientY : e.clientY) - rect.top) * (c.height / rect.height);
    return { x, y };
  };
  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y); ctx.stroke();
    if (!hasContent) { setHasContent(true); }
    onChange(canvasRef.current.toDataURL("image/png"), true);
  };
  const end = () => { drawingRef.current = false; if (hasContent) onChange(canvasRef.current.toDataURL("image/png"), true); };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);
    setHasContent(false);
    onChange("", false);
  };

  return (
    <div>
      <canvas
        ref={canvasRef} width={600} height={180}
        className="w-full border-2 border-dashed border-slate-300 rounded-xl bg-white touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="mandate-signature-canvas"
      />
      <div className="flex items-center justify-between mt-1.5">
        <p className="text-xs text-slate-400">Signez ci-dessus au doigt ou à la souris</p>
        <button type="button" onClick={clear} className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1" data-testid="mandate-signature-clear">
          <Eraser className="w-3.5 h-3.5" /> Effacer
        </button>
      </div>
    </div>
  );
}

export default function FiscalMandatePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [info, setInfo] = useState(null);
  const [mode, setMode] = useState("choice"); // choice | form | done
  const [submitting, setSubmitting] = useState(false);

  // form state
  const [personal, setPersonal] = useState({ full_name: "", address: "", city: "", province: "QC", postal_code: "", birth_date: "", email: "", phone: "", sin: "" });
  const [maritalStatus, setMaritalStatus] = useState("");
  const [hasDependents, setHasDependents] = useState("");
  const [childrenCount, setChildrenCount] = useState("");
  const [services, setServices] = useState([]);
  const [servicesOther, setServicesOther] = useState("");
  const [changes, setChanges] = useState("");
  const [changesDetails, setChangesDetails] = useState("");
  const [directDeposit, setDirectDeposit] = useState([]);
  const [spouse, setSpouse] = useState({ full_name: "", address: "", estimated_income: "", sin: "" });
  const [dependents, setDependents] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [signature, setSignature] = useState("");
  const [hasSignature, setHasSignature] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/fiscal-mandate/${token}`);
        setInfo(r.data);
        if (r.data.status === "completed") setMode("done");
        const pf = r.data.prefill || {};
        if (Object.keys(pf).length) setPersonal((p) => ({ ...p, ...Object.fromEntries(Object.entries(pf).filter(([, v]) => v)) }));
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const toggle = (setter) => (val) => setter((arr) => arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

  const downloadBlank = () => window.open(`${API}/public/fiscal-mandate/${token}/blank-pdf`, "_blank");

  const submit = async () => {
    if (!personal.full_name.trim()) { toast.error("Veuillez entrer votre nom complet"); return; }
    if (!hasSignature) { toast.error("Veuillez signer le formulaire"); return; }
    if (!consent) { toast.error("Veuillez accepter l'autorisation"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/fiscal-mandate/${token}/submit`, {
        form_data: {
          personal,
          marital_status: maritalStatus,
          has_dependents: hasDependents,
          children_count: childrenCount,
          services,
          services_other: servicesOther,
          changes,
          changes_details: changesDetails,
          direct_deposit: directDeposit,
          spouse,
          dependents,
          documents,
        },
        signature_image_b64: signature,
        consent: true,
      });
      setMode("done");
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi — réessayez");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center max-w-md" data-testid="mandate-not-found">
          <FileSignature className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <h1 className="text-lg font-bold text-slate-800 mb-1">Lien invalide</h1>
          <p className="text-sm text-slate-500">Ce lien de formulaire n'existe pas ou n'est plus disponible. Contactez DG Comptabilité au 418 376-4298.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="fiscal-mandate-page">
      {/* Header */}
      <div className="text-white py-6 px-4" style={{ background: NAVY }}>
        <div className="max-w-3xl mx-auto">
          <h1 className="text-xl sm:text-2xl font-bold">DG Comptabilité</h1>
          <p className="text-sm opacity-80 mt-0.5">
            Formulaire de renseignements clients — Année d'imposition {info.tax_year}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 mt-6 space-y-4">
        {mode === "done" && (
          <div className="bg-white rounded-2xl border-2 border-emerald-200 p-8 text-center" data-testid="mandate-done">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500 mb-3" />
            <h2 className="text-xl font-bold text-slate-800 mb-1">Merci !</h2>
            <p className="text-sm text-slate-600">
              Votre formulaire a bien été reçu, rempli et signé.<br />
              DG Comptabilité vous contactera si des informations supplémentaires sont nécessaires.
            </p>
          </div>
        )}

        {mode === "choice" && (
          <>
            <p className="text-sm text-slate-600">
              Bonjour {info.client_name} 👋 — pour préparer votre dossier fiscal, choisissez l'option qui vous convient :
            </p>
            {info.notes && <p className="text-sm text-slate-500 italic bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">« {info.notes} »</p>}
            <div className="grid sm:grid-cols-2 gap-4">
              <button onClick={() => setMode("form")}
                className="bg-white rounded-2xl border-2 border-slate-200 hover:border-blue-900 p-6 text-center transition hover:shadow-lg group"
                data-testid="mandate-choice-online">
                <div className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center text-white" style={{ background: NAVY }}>
                  <PenLine className="w-7 h-7" />
                </div>
                <h3 className="font-bold text-slate-800">Remplir en ligne</h3>
                <p className="text-xs text-slate-500 mt-1">Formulaire à l'écran + signature au doigt ou à la souris — 5 minutes</p>
              </button>
              <button onClick={downloadBlank}
                className="bg-white rounded-2xl border-2 border-slate-200 hover:border-slate-400 p-6 text-center transition hover:shadow-lg"
                data-testid="mandate-choice-print">
                <div className="w-14 h-14 rounded-2xl mx-auto mb-3 bg-slate-100 flex items-center justify-center text-slate-600">
                  <Printer className="w-7 h-7" />
                </div>
                <h3 className="font-bold text-slate-800">Imprimer et retourner</h3>
                <p className="text-xs text-slate-500 mt-1">Téléchargez le PDF, remplissez-le à la main et retournez-le nous</p>
              </button>
            </div>
          </>
        )}

        {mode === "form" && (
          <div className="space-y-4">
            <Section num="1" title="INFORMATIONS PERSONNELLES">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Nom complet *"><input className={inputCls} value={personal.full_name} onChange={(e) => setPersonal({ ...personal, full_name: e.target.value })} data-testid="mandate-full-name" /></Field>
                <Field label="Date de naissance"><input type="date" className={inputCls} value={personal.birth_date} onChange={(e) => setPersonal({ ...personal, birth_date: e.target.value })} data-testid="mandate-birth-date" /></Field>
              </div>
              <Field label="Adresse"><input className={inputCls} value={personal.address} onChange={(e) => setPersonal({ ...personal, address: e.target.value })} data-testid="mandate-address" /></Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Ville"><input className={inputCls} value={personal.city} onChange={(e) => setPersonal({ ...personal, city: e.target.value })} data-testid="mandate-city" /></Field>
                <Field label="Province"><input className={inputCls} value={personal.province} onChange={(e) => setPersonal({ ...personal, province: e.target.value })} /></Field>
                <Field label="Code postal"><input className={inputCls} value={personal.postal_code} onChange={(e) => setPersonal({ ...personal, postal_code: e.target.value })} /></Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <Field label="Courriel"><input type="email" className={inputCls} value={personal.email} onChange={(e) => setPersonal({ ...personal, email: e.target.value })} data-testid="mandate-email" /></Field>
                <Field label="Téléphone"><input className={inputCls} value={personal.phone} onChange={(e) => setPersonal({ ...personal, phone: e.target.value })} /></Field>
                <Field label="N.A.S."><input className={inputCls} value={personal.sin} onChange={(e) => setPersonal({ ...personal, sin: e.target.value })} placeholder="___ ___ ___" data-testid="mandate-sin" /></Field>
              </div>
            </Section>

            <Section num="2" title="STATUT FISCAL">
              <div className="flex flex-wrap gap-2">
                {MARITAL.map((m) => (
                  <button key={m} type="button" onClick={() => setMaritalStatus(m)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${maritalStatus === m ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}
                    style={maritalStatus === m ? { background: NAVY } : {}}
                    data-testid={`mandate-marital-${m}`}>
                    {m}
                  </button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3 items-end">
                <Field label="Avez-vous des personnes à charge ?">
                  <div className="flex gap-2">
                    {["Oui", "Non"].map((v) => (
                      <button key={v} type="button" onClick={() => setHasDependents(v)}
                        className={`text-sm px-4 py-1.5 rounded-lg border ${hasDependents === v ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-300"}`}
                        style={hasDependents === v ? { background: NAVY } : {}}
                        data-testid={`mandate-dependents-${v}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Nombre d'enfants"><input type="number" min="0" className={inputCls} value={childrenCount} onChange={(e) => setChildrenCount(e.target.value)} data-testid="mandate-children-count" /></Field>
              </div>
            </Section>

            <Section num="3" title="SERVICES DEMANDÉS">
              <CheckList options={SERVICES} selected={services} onToggle={toggle(setServices)} testPrefix="mandate-service" />
              <Field label="Autres services (précisez)"><input className={inputCls} value={servicesOther} onChange={(e) => setServicesOther(e.target.value)} /></Field>
            </Section>

            <Section num="4" title="CHANGEMENTS AU 31 DÉCEMBRE">
              <Field label="Y a-t-il eu des changements importants durant l'année ?">
                <div className="flex gap-2">
                  {["Non", "Oui"].map((v) => (
                    <button key={v} type="button" onClick={() => setChanges(v)}
                      className={`text-sm px-4 py-1.5 rounded-lg border ${changes === v ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-300"}`}
                      style={changes === v ? { background: NAVY } : {}}
                      data-testid={`mandate-changes-${v}`}>
                      {v}
                    </button>
                  ))}
                </div>
              </Field>
              {changes === "Oui" && (
                <Field label="Précisez"><textarea rows={2} className={inputCls} value={changesDetails} onChange={(e) => setChangesDetails(e.target.value)} data-testid="mandate-changes-details" /></Field>
              )}
            </Section>

            <Section num="5" title="DÉPÔT DIRECT">
              <CheckList options={DEPOSIT} selected={directDeposit} onToggle={toggle(setDirectDeposit)} testPrefix="mandate-deposit" />
            </Section>

            <Section num="6" title="CONJOINT(E) — À REMPLIR SI DÉCLARATION SÉPARÉE">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Nom complet"><input className={inputCls} value={spouse.full_name} onChange={(e) => setSpouse({ ...spouse, full_name: e.target.value })} data-testid="mandate-spouse-name" /></Field>
                <Field label="Adresse (si différente)"><input className={inputCls} value={spouse.address} onChange={(e) => setSpouse({ ...spouse, address: e.target.value })} /></Field>
                <Field label="Revenu estimé"><input className={inputCls} value={spouse.estimated_income} onChange={(e) => setSpouse({ ...spouse, estimated_income: e.target.value })} /></Field>
                <Field label="N.A.S."><input className={inputCls} value={spouse.sin} onChange={(e) => setSpouse({ ...spouse, sin: e.target.value })} placeholder="___ ___ ___" /></Field>
              </div>
            </Section>

            <Section title="RENSEIGNEMENTS ENFANTS / PERSONNES À CHARGE">
              {dependents.map((d, i) => (
                <div key={i} className="border border-slate-200 rounded-xl p-3 space-y-3 relative" data-testid={`mandate-dependent-${i}`}>
                  <button type="button" onClick={() => setDependents(dependents.filter((_, j) => j !== i))}
                    className="absolute top-2 right-2 text-slate-300 hover:text-red-500" data-testid={`mandate-dependent-remove-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <p className="text-xs font-bold text-slate-500">Personne à charge #{i + 1}</p>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <Field label="Nom complet"><input className={inputCls} value={d.full_name} onChange={(e) => setDependents(dependents.map((x, j) => j === i ? { ...x, full_name: e.target.value } : x))} data-testid={`mandate-dependent-name-${i}`} /></Field>
                    <Field label="Sexe">
                      <div className="flex gap-2">
                        {["F", "M"].map((s) => (
                          <button key={s} type="button" onClick={() => setDependents(dependents.map((x, j) => j === i ? { ...x, sex: s } : x))}
                            className={`text-sm px-4 py-1.5 rounded-lg border ${d.sex === s ? "text-white border-transparent" : "bg-white text-slate-600 border-slate-300"}`}
                            style={d.sex === s ? { background: NAVY } : {}}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </Field>
                    <Field label="Date de naissance"><input type="date" className={inputCls} value={d.birth_date} onChange={(e) => setDependents(dependents.map((x, j) => j === i ? { ...x, birth_date: e.target.value } : x))} /></Field>
                    <Field label="Lien avec la personne"><input className={inputCls} value={d.relation} onChange={(e) => setDependents(dependents.map((x, j) => j === i ? { ...x, relation: e.target.value } : x))} placeholder="ex : fils, mère" /></Field>
                    <Field label="Fréquente un établissement scolaire"><input className={inputCls} value={d.school} onChange={(e) => setDependents(dependents.map((x, j) => j === i ? { ...x, school: e.target.value } : x))} placeholder="Oui / Non / lequel" /></Field>
                    <Field label="Revenu estimé"><input className={inputCls} value={d.estimated_income} onChange={(e) => setDependents(dependents.map((x, j) => j === i ? { ...x, estimated_income: e.target.value } : x))} /></Field>
                  </div>
                </div>
              ))}
              <button type="button" onClick={() => setDependents([...dependents, { ...EMPTY_DEP }])}
                className="text-sm text-slate-600 border border-dashed border-slate-300 rounded-xl px-4 py-2.5 w-full hover:bg-slate-50 inline-flex items-center justify-center gap-1.5"
                data-testid="mandate-add-dependent">
                <Plus className="w-4 h-4" /> Ajouter une personne à charge
              </button>
            </Section>

            <Section title="DOCUMENTS À FOURNIR">
              <p className="text-xs text-slate-500">Liste de base — d'autres documents pourraient être requis selon votre situation. Cochez ce qui s'applique à vous :</p>
              <CheckList options={DOCUMENTS} selected={documents} onToggle={toggle(setDocuments)} testPrefix="mandate-doc" />
            </Section>

            <Section title="LOI 25 ET AUTORISATION">
              <p className="text-xs text-slate-600 leading-relaxed">
                Conformément à la Loi 25 du Québec, nous nous engageons à respecter et protéger les renseignements
                personnels que vous nous transmettez. Les renseignements transmis seront utilisés uniquement dans le
                cadre de la préparation de vos déclarations fiscales et seront conservés de manière sécurisée.
              </p>
              <p className="text-xs text-slate-600 leading-relaxed">
                J'autorise DG Comptabilité (Dominik Gougeon) à préparer mes déclarations fiscales fédérale et
                provinciale pour l'année d'imposition <b>{info.tax_year}</b> et j'atteste que les informations fournies
                sont exactes. Je confirme également que DG Comptabilité (Dominik Gougeon) est autorisé à transmettre
                électroniquement mes déclarations aux gouvernements fédéral et provincial.
              </p>
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-blue-900" data-testid="mandate-consent" />
                <span>J'ai lu et j'accepte l'autorisation ci-dessus</span>
              </label>
              <SignatureCanvas onChange={(dataUrl, has) => { setSignature(dataUrl); setHasSignature(has); }} />
            </Section>

            <div className="flex flex-col sm:flex-row gap-2 sm:justify-between items-center pt-2">
              <button type="button" onClick={() => setMode("choice")} className="text-sm text-slate-500 hover:text-slate-700 underline" data-testid="mandate-back-btn">
                ← Retour aux options
              </button>
              <button onClick={submit} disabled={submitting || !hasSignature || !consent}
                className="px-6 py-3 rounded-xl text-white font-bold inline-flex items-center gap-2 disabled:opacity-40 w-full sm:w-auto justify-center"
                style={{ background: NAVY }}
                data-testid="mandate-submit-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                {submitting ? "Envoi en cours..." : "Soumettre mon formulaire signé"}
              </button>
            </div>
          </div>
        )}

        <p className="text-[11px] text-slate-400 text-center pt-4">
          418 376-4298 · d.gougeon@outlook.fr · 412, rue J-R. Théberge, Chicoutimi, QC, G7J 3R9
        </p>
      </div>
    </div>
  );
}
